import React, { MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useCoordsRetriever } from './hooks/useCoordsRetriever';
import MapContainer from './components/MapContainer/MapContainer';
import SearchBar from './components/SearchBar/SearchBar';
import { useShouldDisableRedirect } from './hooks/useShouldDisableRedirect';
import { setWelcomeVisit } from './store/slices/sessionStatus';
import { useAppSelector } from './hooks/useAppSelector';
import { useAppDispatch } from './hooks/useAppDispatch';
import { Flip, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Map } from 'mapbox-gl';
import PlaceholderImage from './components/MapPlaceholderImage';
import { Footer } from './components/Footer';
import { HelpOverlay } from './components/HelpOverlay';
import Box from '@mui/material/Box';
import Menu from './components/menu';
import { useTheme, useMediaQuery } from '@mui/material';

interface MainProps {
    children: ReactNode;
    mapRef: MutableRefObject<Map | null>;
}

function Main({ children, mapRef }: MainProps) {
    const sessionStatus = useAppSelector((state) => state.sessionStatus);
    const map = useAppSelector((state) => state.map);
    const searchBar = useAppSelector((state) => state.searchBar);
    const { overrideNoHomeRedirect } = searchBar;
    const theme = useTheme();
    const isLg = useMediaQuery(theme.breakpoints.down('lg'));

    const redirected = useRef(false);
    const [siteLoaded, setSiteLoaded] = useState(false);

    const shouldDisableRedirect = useShouldDisableRedirect();

    const { dateLastVisit } = sessionStatus;
    const location = useLocation();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const { pathname } = location;

    const hasValidCenter = map.center?.lat != null && map.center?.lng != null;

    // 1. Redirect to welcome page (if home is already available)
    useEffect(() => {
        const dateNow = new Date();
        const dateThen = new Date(dateLastVisit);
        dateThen.setDate(dateThen.getDate() + 1);

        const isTimeForWelcome = !dateLastVisit || (dateNow.getTime() > dateThen.getTime());

        if (
            pathname === '/' &&
            hasValidCenter &&
            isTimeForWelcome &&
            !shouldDisableRedirect
        ) {
            history.push('/welcome');
            redirected.current = true;
            dispatch(setWelcomeVisit());
        }
    }, [dateLastVisit, hasValidCenter, pathname]);

    // 2. Redirect to /no-home-selected if no center is set
    useEffect(() => {
        const shouldRedirect =
            !hasValidCenter &&
            ((!redirected.current && !siteLoaded) || siteLoaded) &&
            !shouldDisableRedirect &&
            !overrideNoHomeRedirect;
        if (shouldRedirect) {
            redirected.current = true;
            history.push('/no-home-selected');
        }
    }, [pathname, hasValidCenter]);

    useEffect(() => {
        setSiteLoaded(true);
    }, []);

    useCoordsRetriever();

    return (
        <>
            <Box sx={{
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexFlow: 'column',
                width: '100%',
            }}
            >
                <Menu mapRef={mapRef}/>
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    left: 0,
                    margin: '0.5em',
                }}
                >
                    <ToastContainer
                        position={isLg?'top-right':'top-center'}
                        limit={1}
                        hideProgressBar={false}
                        newestOnTop
                        closeOnClick
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                        autoClose={2500}
                        theme='dark'
                        transition={Flip}
                    />
                </Box>
                {children}
                <SearchBar mapRef={mapRef}/>
                <HelpOverlay/>
                {!hasValidCenter
                    ? <PlaceholderImage/>
                    : <MapContainer mapRef={mapRef}/>}
                <Footer mapRef={mapRef}/>
            </Box>
        </>
    );
}

export default Main;
